import { useBloc } from "@blac/react";
import type {
  ConversationResponse,
  NotificationMessageResponse
} from "@9amhealth/openapi";
import { Cubit } from "blac-next";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import NotificationsCubit from "src/state/NotificationsCubit/NotificationsCubit";
import ConversationListCubit from "../LeapChat/state/ConversationListCubit";
import { StorageController } from "src/state/StorageBloc/StorageBloc";
import { tracker } from "src/state/state";
import { APP_CHAT_PATH } from "src/lib/createAppPath";

// --- Constants ---

export const SESSION_KEY_CHECK_DONE = "appNotificationsRedirectDone";

const ELIGIBLE_PATHS = ["/app/home", APP_CHAT_PATH] as const;
const PATH_CHAT = APP_CHAT_PATH;
const PATH_HOME = "/app/home";
const ANALYTICS_EVENT = "Notifications Redirect Check";

// --- Types ---

interface AppNotificationsRedirectState {
  redirectTo: URL | null;
}

interface RedirectCheckInput {
  conversations: ConversationResponse[];
  notifications: NotificationMessageResponse[];
  location?: Location;
}

// --- Cubit ---

export class AppNotificationsRedirectCubit extends Cubit<AppNotificationsRedirectState> {
  static keepAlive = true;

  constructor() {
    super({ redirectTo: null });
  }

  private get isCheckDone(): boolean {
    return (
      StorageController.getItem(SESSION_KEY_CHECK_DONE, sessionStorage) === "1"
    );
  }

  private markCheckDone(): void {
    StorageController.setItem(SESSION_KEY_CHECK_DONE, "1", sessionStorage);
  }

  checkForRedirect = ({
    conversations,
    notifications,
    location = window.location
  }: RedirectCheckInput): void => {
    let shouldRedirect = true;

    if (this.isCheckDone) {
      shouldRedirect = false;
    }

    const isEligiblePath = ELIGIBLE_PATHS.some((path) =>
      location.pathname.startsWith(path)
    );
    if (!isEligiblePath) {
      shouldRedirect = false;
    }

    this.markCheckDone();

    const unreadConversationsCount = conversations.reduce(
      (count, conversation) => count + conversation.unreadMessageCount,
      0
    );
    const unreadNotificationsCount = notifications.length;

    // Only redirect when exactly one type has unread items
    if (unreadConversationsCount === unreadNotificationsCount) {
      shouldRedirect = false;
    }

    const targetPath = unreadConversationsCount > 0 ? PATH_CHAT : PATH_HOME;
    if (location.pathname === targetPath) {
      shouldRedirect = false;
    }

    const targetUrl = new URL(location.href);
    targetUrl.pathname = targetPath;

    tracker.track(ANALYTICS_EVENT, {
      data: {
        unreadConversationsCount,
        unreadNotificationsCount,
        currentPath: location.pathname,
        goTo: targetPath,
        willRedirect: shouldRedirect
      }
    });

    if (shouldRedirect) {
      this.patch({ redirectTo: targetUrl });
    }
  };
}

// --- Component ---

const AppNotificationsRedirect = () => {
  const [conversationState] = useBloc(ConversationListCubit);
  const [notificationState] = useBloc(NotificationsCubit);
  const [{ redirectTo }, { checkForRedirect }] = useBloc(
    AppNotificationsRedirectCubit
  );
  const navigate = useNavigate();
  const didRedirectRef = useRef(false);

  useEffect(() => {
    if (
      conversationState.initialLoadComplete &&
      notificationState.initialLoadComplete
    ) {
      checkForRedirect({
        conversations: conversationState.conversations,
        notifications: notificationState.notifications
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    checkForRedirect,
    conversationState.initialLoadComplete,
    notificationState.initialLoadComplete
  ]);

  useEffect(() => {
    if (redirectTo && !didRedirectRef.current) {
      didRedirectRef.current = true;
      navigate(`${redirectTo.pathname}${redirectTo.search}`, { replace: true });
    }
  }, [redirectTo, navigate]);

  return null;
};

export default AppNotificationsRedirect;
